import { MAX_ROW_LIMIT, buildResource, fetchRowChunk, runQueries } from '../GscTools/query';

/**
 * The requests this report makes, one function per block.
 *
 * Every one of them names the selected source as its `type`, and nothing here ever asks two
 * sources in one call: a Discover row and a Google News row are counts of different products, and
 * the only thing keeping them apart is the request that fetched them.
 *
 * `auto` aggregation throughout. Search Overview asks for its headline `byProperty`, and that is
 * right for Web Search; it is not something to force on Discover or Google News, whose
 * aggregation Search Console decides for itself. Letting it decide is also what keeps the
 * headline, the trend and the page and country rows below them read the same way.
 */

/** The parts of the scope every request on this report carries. */
function scopeRequest(scope) {
    return {
        aggregationType: 'auto',
        dataState: scope.dataState,
        searchType: scope.searchType,
        country: scope.country,
    };
}

/**
 * Blocks 01 and 02 — one window's totals and its daily series, in one call.
 *
 * The totals are asked for with no dimension at all, which is the only request that answers "how
 * much did this source get". They are never rebuilt from the page rows below, which are a subset
 * Search Console was willing to return, and never from the daily rows, whose sum is the total of
 * the days it returned rather than the window's own figure.
 *
 * Current and baseline go in separate calls so a baseline that fails leaves the current window's
 * summary standing.
 */
export async function fetchPeriod({ credentialsDomainId, scope, period }) {
    const common = { period, ...scopeRequest(scope) };

    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            { key: 'totals', resource: buildResource({ ...common, dimensions: [] }) },
            { key: 'daily', resource: buildResource({ ...common, dimensions: ['date'] }) },
        ],
    });

    return { totals: results.totals ?? [], daily: results.daily ?? [] };
}

/** Block 03 — one chunk of page rows for one window, paged by the shared row cursor. */
export function fetchPageChunk({ credentialsDomainId, scope, period, offset = 0 }) {
    return fetchRowChunk({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        period,
        dimension: 'page',
        searchType: scope.searchType,
        country: scope.country,
        dataState: scope.dataState,
        offset,
    });
}

/**
 * Block 03's drilldown — one page's daily series in each window.
 *
 * A separate filtered request rather than a regrouping of the page rows: those came back keyed by
 * page with no date dimension, so the days inside them were never sent. The URL is passed back
 * exactly as Search Console returned it, which is what its `equals` filter compares against.
 */
export async function fetchPageSeries({ credentialsDomainId, scope, page }) {
    const daily = (period) =>
        buildResource({
            period,
            dimensions: ['date'],
            ...scopeRequest(scope),
            filters: [{ dimension: 'page', operator: 'equals', expression: page }],
        });

    const queries = [{ key: 'current', resource: daily(scope.current) }];
    if (scope.baseline) queries.push({ key: 'baseline', resource: daily(scope.baseline) });

    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries,
    });

    return { current: results.current ?? [], baseline: results.baseline ?? [] };
}

/**
 * Block 04 — the current window's traffic keyed by country.
 *
 * Its own request, not a regrouping of anything above: the countries are a separate cut of the
 * source's traffic, and summing page rows by country would only ever see the pages that were
 * returned. One window only — the block says where the traffic was served, not how that moved.
 */
export async function fetchCountries({ credentialsDomainId, scope }) {
    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'countries',
                resource: buildResource({
                    period: scope.current,
                    dimensions: ['country'],
                    ...scopeRequest(scope),
                }),
            },
        ],
    });

    return results.countries ?? [];
}
